import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PublicToolsMenu from '../components/public/PublicToolsMenu';
import { AskAvailabilityButton, SavePhotoButton, resolveImage } from '../components/public/CatalogHelpers';
import { ProductImageCarousel, ProductInfoButton } from '../components/public/ProductMediaCard';
import { generateOffersFile } from '../components/public/OffersExporter';
import { useTrackPage } from '../utils/sellerTracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPE_BADGES = {
  local:    { icon: '🏪', label: 'Solo tienda',    className: 'bg-purple-100 text-purple-700 border-purple-200' },
  shipment: { icon: '🚚', label: 'Solo envío',     className: 'bg-blue-100 text-blue-700 border-blue-200' },
  both:     { icon: '✅', label: 'Tienda y envío', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

function DaysLeftBadge({ days }) {
  if (days === null || days === undefined) {
    return <span className="text-[11px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Sin fecha límite</span>;
  }
  if (days < 0) {
    return <span className="text-[11px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Expirada</span>;
  }
  let cls = 'bg-emerald-100 text-emerald-700';
  let label = `${days} días restantes`;
  if (days === 0) { cls = 'bg-red-100 text-red-700'; label = '¡Último día!'; }
  else if (days === 1) { cls = 'bg-red-100 text-red-700'; label = '¡Mañana termina!'; }
  else if (days <= 3) { cls = 'bg-orange-100 text-orange-700'; label = `¡${days} días!`; }
  else if (days <= 7) { cls = 'bg-amber-100 text-amber-700'; label = `${days} días restantes`; }
  return (
    <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${cls}`}>
      ⏳ {label}
    </span>
  );
}

function PromoCard({ p }) {
  const scope = SCOPE_BADGES[p.scope] || SCOPE_BADGES.both;
  const isNewOnly = p.is_new_arrival && !p.has_promo;
  const isGift = p.promo_type === 'gift' || p.promo_type === 'free_shipping';
  const isFreeShip = p.promo_type === 'free_shipping';
  // Para regalos, mostramos el precio base (el "descuento" es el regalo).
  const hasBase = !isGift && p.base_price > p.promo_price && p.promo_price > 0;
  const discount = hasBase
    ? Math.round((1 - p.promo_price / p.base_price) * 100)
    : 0;
  const displayPrice = isNewOnly
    ? p.base_price
    : (isGift ? p.base_price : p.promo_price);
  const combo = p.promo_type === 'combo' ? p.promo_combo_product : null;
  const comboBase = combo ? p.base_price + (combo.precio_1 || 0) : 0;
  const comboTotal = combo ? comboBase - (p.promo_combo_discount || 0) : 0;
  const gift = p.promo_gift_product;
  const giftImg = gift?.image_url ? resolveImage(gift.image_url) : null;

  return (
    <article className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-md transition" data-testid={`promo-card-${p.id}`}>
      {combo && (
        <div className="relative aspect-square bg-slate-50" data-testid={`promo-combo-images-${p.id}`}>
          {[{ name: combo.name, img: combo.image_url, pos: 'top-[3%] left-[3%]' }, { name: p.name, img: p.image_url, pos: 'bottom-[3%] right-[3%]' }].map((x, i) => (
            <div key={i} className={`absolute ${x.pos} w-[47%] aspect-square bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden flex items-center justify-center`}>
              {x.img ? <img src={resolveImage(x.img)} alt={x.name} className="w-full h-full object-contain p-1" loading="lazy" />
                : <span className="text-4xl">🏷️</span>}
            </div>
          ))}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-orange-500 text-white font-black text-2xl flex items-center justify-center shadow-lg border-2 border-white z-10">+</div>
          <div className="absolute bottom-2 left-2 bg-orange-500 text-white text-xs font-bold rounded-full px-2 py-1 shadow-md z-10">🔗 COMBO</div>
          {p.promo_combo_discount > 0 && (
            <div className="absolute top-2 right-12 bg-red-600 text-white text-xs font-bold rounded-full px-2 py-1 shadow-md z-10">-${Math.round(p.promo_combo_discount)}</div>
          )}
          <ProductInfoButton productName={p.name} info={p.product_info} sku={p.sku} purchaseCost={p.purchase_cost} stockM={p.stock_m} stockA={p.stock_a} commission={p.default_commission} wholesale={p.mayoreo} />
        </div>
      )}
      {/* Imagen con carrusel + badges superpuestos */}
      {!combo && <ProductImageCarousel
        mainImage={p.image_url}
        extraImages={p.extra_images}
        alt={p.name}
        fallbackEmoji="🏷️"
      >
        {discount > 0 && (
          <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold rounded-full px-2 py-1 shadow-md pointer-events-none z-10">
            -{discount}%
          </div>
        )}
        {p.is_new_arrival && (
          <div className="absolute left-2 bg-emerald-500 text-white text-[10px] font-black rounded-full px-2.5 py-1 shadow-md tracking-wider pointer-events-none z-10" style={{ top: discount > 0 ? '2.25rem' : '0.5rem' }}>
            🆕 NUEVO
          </div>
        )}
        {/* Badge de regalo con imagen circular del producto de regalo */}
        {isGift && gift && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-white rounded-full shadow-lg border-2 border-amber-400 pl-1 pr-2 py-0.5 z-10" title={`Regalo: ${gift.name}`} data-testid={`promo-gift-${p.id}`}>
            <div className="w-9 h-9 rounded-full overflow-hidden bg-amber-50 flex items-center justify-center border border-amber-200">
              {giftImg ? (
                <img src={giftImg} alt={gift.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-lg">🎁</span>
              )}
            </div>
            <span className="text-[11px] font-bold text-amber-700">+{gift.quantity || 1} GRATIS</span>
          </div>
        )}
        {/* Badge PAQUETE removido a petición del user: los paquetes se ven igual que productos */}
        <ProductInfoButton productName={p.name} info={p.product_info} sku={p.sku} purchaseCost={p.purchase_cost} stockM={p.stock_m} stockA={p.stock_a} commission={p.default_commission} wholesale={p.mayoreo} />
      </ProductImageCarousel>}

      {/* Body */}
      <div className="p-3 flex-1 flex flex-col gap-2">
        <h3 className="font-bold text-slate-900 leading-tight break-words">{combo ? `${combo.name} + ${p.name}` : p.name}</h3>
        {combo && (
          <div className="text-xs text-slate-700 bg-orange-50 border border-orange-200 rounded-md px-2 py-1.5 space-y-0.5" data-testid={`promo-combo-${p.id}`}>
            <div className="font-semibold text-orange-800">🔗 El combo incluye:</div>
            <div className="flex justify-between gap-2"><span className="truncate">• {combo.name}</span><span>${(combo.precio_1 || 0).toFixed(2)}</span></div>
            <div className="flex justify-between gap-2"><span className="truncate">• {p.name}</span><span><span className="line-through text-slate-400 mr-1">${p.base_price.toFixed(2)}</span>${p.promo_price.toFixed(2)}</span></div>
            <div className="text-[11px] text-orange-700">Ahorras <b>${(p.promo_combo_discount || 0).toFixed(2)}</b> llevando los dos</div>
          </div>
        )}

        {/* Precios */}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`text-2xl font-bold ${isNewOnly ? 'text-emerald-700' : 'text-red-700'}`}>${(combo ? comboTotal : displayPrice).toFixed(2)}</span>
          {combo && <span className="text-sm text-slate-400 line-through">${comboBase.toFixed(2)}</span>}
          {hasBase && !isNewOnly && !combo && (
            <span className="text-sm text-slate-400 line-through">${p.base_price.toFixed(2)}</span>
          )}
        </div>

        {/* Descripción de la promo según tipo */}
        {p.promo_type === 'min_qty' && p.promo_min_qty > 0 && (
          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-md px-2 py-1" data-testid={`promo-minqty-${p.id}`}>
            🔢 Comprando <b>{p.promo_min_qty} o más</b>
          </p>
        )}
        {p.promo_type === 'percent_discount' && p.promo_discount_percent > 0 && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-2 py-1" data-testid={`promo-percent-${p.id}`}>
            🏷️ <b>{Math.round(p.promo_discount_percent)}% de descuento</b>
          </p>
        )}
        {p.promo_type === 'limited_qty' && (
          <p className="text-xs text-orange-700 bg-orange-50 border border-orange-100 rounded-md px-2 py-1" data-testid={`promo-limited-${p.id}`}>
            🔥 {typeof p.promo_qty_remaining === 'number'
              ? (p.promo_qty_remaining > 0
                  ? <>¡Solo quedan <b>{p.promo_qty_remaining}</b> pieza{p.promo_qty_remaining === 1 ? '' : 's'}!</>
                  : <>Cupo agotado</>)
              : <>Cupo limitado</>}
            {p.promo_qty_limit ? <span className="text-orange-500"> · {p.promo_qty_sold || 0}/{p.promo_qty_limit}</span> : null}
          </p>
        )}
        {isFreeShip && (
          <p className="text-xs font-semibold text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-2 py-1" data-testid={`promo-free-shipping-${p.id}`}>
            🚚 <b>Envío gratis</b> comprando este producto
          </p>
        )}
        {isGift && gift && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid={`promo-gift-text-${p.id}`}>
            🎁 Llévate <b>{gift.quantity > 1 ? `${gift.quantity}× ` : ''}{gift.name}</b> de regalo
          </p>
        )}

        {/* Badge de validez */}
        <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
          {p.has_promo ? (
            <>
              <span className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 ${scope.className}`}>
                <span>{scope.icon}</span> {scope.label}
              </span>
              <DaysLeftBadge days={p.days_left} />
            </>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
              ✨ Recién llegado
            </span>
          )}
        </div>

        {/* Botones de acción */}
        <div className="grid grid-cols-1 gap-1.5 pt-1">
          <AskAvailabilityButton productName={p.name} price={displayPrice} />
          <SavePhotoButton imageUrl={p.image_url} filename={p.name} />
        </div>
      </div>
    </article>
  );
}

export default function ActivePromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [pinDialog, setPinDialog] = useState(null); // { format } | null
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinChecking, setPinChecking] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'new'

  // Trackear tiempo en ofertas si el dispositivo pertenece a un vendedor
  useTrackPage('offers_view');

  const doExport = async (format, seller = null) => {
    setExporting(true);
    setExportProgress({ page: 0, total: 0 });
    try {
      await generateOffersFile({
        promos, format, seller,
        onProgress: (p) => setExportProgress(p),
      });
    } catch (e) {
      alert(e.message || 'No se pudo generar el archivo');
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const handleExport = (format) => {
    if (exporting || promos.length === 0) return;
    setPinValue('');
    setPinError('');
    setPinDialog({ format });
  };

  const submitPin = async (skip = false) => {
    const format = pinDialog?.format || 'pdf';
    if (skip) {
      setPinDialog(null);
      await doExport(format, null);
      return;
    }
    if (!/^\d{4}$/.test(pinValue)) {
      setPinError('El NIP debe ser 4 dígitos');
      return;
    }
    setPinChecking(true); setPinError('');
    try {
      const { data } = await axios.post(`${API}/public/seller/login`, { nip: pinValue });
      const seller = data?.seller || null;
      setPinDialog(null);
      await doExport(format, seller);
    } catch (e) {
      setPinError(e?.response?.data?.detail || 'No se pudo validar el NIP');
    } finally {
      setPinChecking(false);
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/public/active-promos`);
        if (!cancel) setPromos(data?.promos || []);
      } catch (e) {
        if (!cancel) setError('No se pudieron cargar las ofertas');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicToolsMenu current="/ofertas" />

      {/* Header */}
      <header
        className="bg-gradient-to-br from-red-600 to-red-700 text-white shadow"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
      >
        <div className="max-w-5xl mx-auto px-4 pb-6 text-center">
          <p className="text-[11px] uppercase tracking-widest opacity-80">Ofertas Activas</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1">🏷️ Promociones vigentes</h1>
          <p className="text-sm opacity-90 mt-2">
            {loading ? 'Cargando…' : (() => {
              const newCount = promos.filter(p => p.is_new_arrival).length;
              const filteredCount = filter === 'new' ? newCount : promos.length;
              return filteredCount > 0
                ? `${filteredCount} oferta${filteredCount === 1 ? '' : 's'} disponible${filteredCount === 1 ? '' : 's'}`
                : (promos.length === 0 ? 'Sin ofertas activas en este momento' : 'Sin resultados en este filtro');
            })()}
          </p>
          {promos.length > 0 && (
            <>
              {/* Filtro Todos / Nuevos */}
              {promos.some(p => p.is_new_arrival) && (
                <div className="mt-3 inline-flex gap-1 rounded-full bg-white/15 p-1 text-xs">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-3 py-1.5 rounded-full font-medium transition ${filter === 'all' ? 'bg-white text-red-700' : 'text-white'}`}
                    data-testid="promos-filter-all"
                  >Todos ({promos.length})</button>
                  <button
                    onClick={() => setFilter('new')}
                    className={`px-3 py-1.5 rounded-full font-medium transition ${filter === 'new' ? 'bg-white text-red-700' : 'text-white'}`}
                    data-testid="promos-filter-new"
                  >🆕 Nuevos ({promos.filter(p => p.is_new_arrival).length})</button>
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => handleExport('pdf')}
                  disabled={exporting}
                  className="bg-white text-red-700 hover:bg-red-50 disabled:opacity-60 disabled:cursor-wait text-sm font-semibold px-4 py-2 rounded-full shadow flex items-center gap-2"
                  data-testid="export-pdf-btn"
                >
                  {exporting && exportProgress ? `⏳ ${exportProgress.page}/${exportProgress.total}` : <>📄 Descargar PDF</>}
                </button>
                <button
                  onClick={() => handleExport('image')}
                  disabled={exporting}
                  className="bg-red-800/40 hover:bg-red-800/60 disabled:opacity-60 disabled:cursor-wait text-white text-sm font-semibold px-4 py-2 rounded-full flex items-center gap-2 border border-white/20"
                  data-testid="export-image-btn"
                >
                  {exporting && exportProgress ? `⏳ ${exportProgress.page}/${exportProgress.total}` : <>🖼️ Descargar Imagen</>}
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Contenido */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm mb-4" data-testid="promos-error">
            {error}
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="aspect-square bg-slate-100 animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-slate-100 rounded animate-pulse" />
                  <div className="h-6 bg-slate-100 rounded animate-pulse w-1/2" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-3/4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && promos.length === 0 && !error && (
          <div className="text-center py-16" data-testid="promos-empty">
            <div className="text-6xl mb-3">🏷️</div>
            <p className="text-slate-500">Aún no hay promociones activas.</p>
            <p className="text-xs text-slate-400 mt-1">¡Vuelve pronto!</p>
          </div>
        )}

        {!loading && promos.length > 0 && (() => {
          const list = filter === 'new' ? promos.filter(p => p.is_new_arrival) : promos;
          if (list.length === 0) {
            return (
              <div className="text-center py-16" data-testid="promos-empty-filter">
                <div className="text-6xl mb-3">🔍</div>
                <p className="text-slate-500">No hay productos nuevos en ofertas.</p>
                <button
                  onClick={() => setFilter('all')}
                  className="mt-3 text-sm text-red-600 hover:underline"
                >Ver todas las ofertas</button>
              </div>
            );
          }
          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="promos-grid">
              {list.map((p) => <PromoCard key={`${p.kind}-${p.id}`} p={p} />)}
            </div>
          );
        })()}
      </main>

      <footer className="text-center text-[11px] text-slate-400 py-6">
        Sistema POS · Ofertas actualizadas al momento
      </footer>

      {/* Diálogo PIN vendedor para personalizar el catálogo */}
      {pinDialog && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={() => !pinChecking && setPinDialog(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="export-pin-dialog">
            <div className="text-center">
              <div className="text-4xl mb-1">💼</div>
              <h3 className="text-lg font-bold text-slate-800">¿Personalizar con tu info?</h3>
              <p className="text-xs text-slate-500 mt-1">Ingresa tu NIP de vendedor y tu nombre + WhatsApp aparecerán en cada página del catálogo.</p>
            </div>
            <input
              type="password" inputMode="numeric" maxLength={4}
              placeholder="NIP (4 dígitos)"
              value={pinValue}
              onChange={(e) => { setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
              className="w-full text-center text-2xl tracking-widest font-bold py-3 border-2 border-slate-200 focus:border-red-500 rounded-xl outline-none"
              data-testid="export-pin-input"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') submitPin(false); }}
            />
            {pinError && <p className="text-xs text-rose-600 text-center" data-testid="export-pin-error">{pinError}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => submitPin(true)}
                disabled={pinChecking}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-xl font-medium text-sm"
                data-testid="export-pin-skip"
              >Omitir</button>
              <button
                onClick={() => submitPin(false)}
                disabled={pinChecking || pinValue.length !== 4}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white py-2.5 rounded-xl font-semibold text-sm"
                data-testid="export-pin-submit"
              >{pinChecking ? 'Validando…' : 'Generar con mi info'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
